/**
 * ToteCraft bag-studio API client (Phase 4).
 */

function csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute('content') : '';
}

/**
 * Default request timeout. Generate/upload override it with a longer window
 * (AI generation + queue polling can legitimately take minutes).
 */
const DEFAULT_TIMEOUT_MS = 120000;
const GENERATION_TIMEOUT_MS = 300000;

/**
 * @param {string} url
 * @param {RequestInit & { timeoutMs?: number }} [options]
 */
export async function bagFetch(url, options) {
    const opts = options || {};
    const headers = Object.assign(
        {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRF-TOKEN': csrfToken(),
        },
        opts.headers ? opts.headers : {},
    );

    const init = Object.assign({}, opts, { headers, credentials: 'same-origin' });
    const timeoutMs = typeof opts.timeoutMs === 'number' ? opts.timeoutMs : DEFAULT_TIMEOUT_MS;
    delete init.timeoutMs;

    if (!init.signal && timeoutMs > 0 && typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function') {
        init.signal = AbortSignal.timeout(timeoutMs);
    }

    let response;
    try {
        response = await fetch(url, init);
    } catch (err) {
        if (err && (err.name === 'TimeoutError' || err.name === 'AbortError')) {
            throw new Error('The request timed out. Please check your connection and try again.');
        }
        throw err;
    }

    let payload = null;
    try {
        payload = await response.json();
    } catch {
        payload = null;
    }

    if (!response.ok) {
        const message = extractErrorMessage(payload, response.status);
        const error = new Error(message);
        error.status = response.status;
        error.payload = payload;
        throw error;
    }

    return payload;
}

/**
 * Prefer Laravel validation `errors.*[0]` over the generic "given data was invalid" message.
 *
 * @param {unknown} payload
 * @param {number} status
 */
function extractErrorMessage(payload, status) {
    if (payload && typeof payload === 'object') {
        const errors = /** @type {Record<string, unknown>} */ (payload).errors;
        if (errors && typeof errors === 'object') {
            const keys = Object.keys(/** @type {Record<string, unknown>} */ (errors));
            for (let i = 0; i < keys.length; i += 1) {
                const list = /** @type {Record<string, unknown>} */ (errors)[keys[i]];
                if (Array.isArray(list) && list[0]) {
                    return String(list[0]);
                }
            }
        }
        const message = /** @type {Record<string, unknown>} */ (payload).message;
        if (typeof message === 'string' && message.trim() !== '') {
            return message.trim();
        }
    }

    if (status === 413) {
        return 'This image is too large. Please upload a smaller photo.';
    }
    if (status >= 500) {
        return 'Something went wrong while processing your image. Please try a smaller JPEG or PNG.';
    }

    return 'Request failed. Please try again.';
}

/**
 * @param {string} templateId
 */
export function createProject(templateId) {
    const id = typeof templateId === 'string' ? templateId.trim() : '';
    if (!id) {
        const error = new Error('Missing tote template.');
        error.status = 422;
        error.payload = {
            message: 'The template id field is required.',
            errors: { template_id: ['The template id field is required.'] },
        };
        return Promise.reject(error);
    }

    return bagFetch('/api/bag/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ template_id: id }),
    });
}

/**
 * @param {string} token
 */
export function fetchProject(token) {
    return bagFetch('/api/bag/projects/' + encodeURIComponent(token));
}

/**
 * L1 mockup preview payload (Phase 9).
 *
 * @param {string} token
 */
export function fetchPreview(token) {
    return bagFetch('/api/bag/projects/' + encodeURIComponent(token) + '/preview');
}

/**
 * @param {string} token
 * @param {{
 *   prompt: string,
 *   style_code?: string|null,
 *   side: 'front'|'back',
 *   file?: File|null,
 *   rightsConfirmed?: boolean,
 *   brief?: Record<string, unknown>|null
 * }} body
 */
export function generateDesign(token, body) {
    const file = body.file instanceof File ? body.file : null;
    const briefJson = body.brief ? JSON.stringify(body.brief) : null;
    if (file) {
        const form = new FormData();
        form.append('prompt', body.prompt || '');
        if (body.style_code) {
            form.append('style_code', body.style_code);
        }
        form.append('side', body.side);
        form.append('reference', file);
        form.append('rights_confirmed', body.rightsConfirmed ? '1' : '0');
        if (briefJson) {
            form.append('brief', briefJson);
        }

        return bagFetch('/api/bag/projects/' + encodeURIComponent(token) + '/generate', {
            method: 'POST',
            body: form,
            timeoutMs: GENERATION_TIMEOUT_MS,
        });
    }

    const payload = {
        prompt: body.prompt,
        style_code: body.style_code || null,
        side: body.side,
    };
    if (body.brief) {
        payload.brief = body.brief;
    }

    return bagFetch('/api/bag/projects/' + encodeURIComponent(token) + '/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        timeoutMs: GENERATION_TIMEOUT_MS,
    });
}

/**
 * @param {string} token
 * @param {{
 *   prompt: string,
 *   style_code?: string|null,
 *   side: 'front'|'back',
 *   has_reference?: boolean
 * }} body
 */
export function suggestDesignBrief(token, body) {
    return bagFetch('/api/bag/projects/' + encodeURIComponent(token) + '/design-brief', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            prompt: body.prompt || '',
            style_code: body.style_code || null,
            side: body.side,
            has_reference: Boolean(body.has_reference),
            composition_pref: 'auto',
        }),
    }).then((payload) => {
        if (!payload || !payload.brief) {
            throw new Error('Design plan was empty. Please try again.');
        }
        return /** @type {Record<string, unknown>} */ (payload.brief);
    });
}

/**
 * @param {string} token
 * @param {{
 *   side: 'front'|'back',
 *   print_scale: number,
 *   design_id?: number|null
 * }} body
 */
export function scalePrint(token, body) {
    const payload = {
        side: body.side,
        print_scale: body.print_scale,
    };
    if (body.design_id) {
        payload.design_id = body.design_id;
    }

    return bagFetch('/api/bag/projects/' + encodeURIComponent(token) + '/scale-print', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        timeoutMs: GENERATION_TIMEOUT_MS,
    });
}

/**
 * @param {string} token
 * @param {{ file: File, side: 'front'|'back', rightsConfirmed: boolean }} payload
 */
export function uploadDesign(token, payload) {
    const form = new FormData();
    form.append('image', payload.file);
    form.append('side', payload.side);
    form.append('rights_confirmed', payload.rightsConfirmed ? '1' : '0');

    return bagFetch('/api/bag/projects/' + encodeURIComponent(token) + '/upload', {
        method: 'POST',
        body: form,
        timeoutMs: GENERATION_TIMEOUT_MS,
    });
}

/**
 * @param {string} token
 * @param {number} designId
 * @param {'front'|'back'} side
 */
export function selectDesign(token, designId, side) {
    return bagFetch('/api/bag/projects/' + encodeURIComponent(token) + '/select-design', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ design_id: designId, side: side }),
    });
}

/**
 * @param {string} token
 * @param {number} [designId]
 */
export function copyToBack(token, designId) {
    const body = {};
    if (designId) {
        body.design_id = designId;
    }

    return bagFetch('/api/bag/projects/' + encodeURIComponent(token) + '/copy-to-back', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
}

/**
 * @param {string} token
 * @param {{
 *   text: string,
 *   font: string,
 *   color: string,
 *   align: string,
 *   size: string,
 *   overlay: boolean,
 *   side: 'front'|'back'
 * }} body
 */
export function addText(token, body) {
    return bagFetch('/api/bag/projects/' + encodeURIComponent(token) + '/add-text', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
    });
}

/**
 * @param {string} token
 * @param {{ canvas_oz?: number, print_sides?: string }} [body]
 */
export function fetchCartLines(token, body) {
    return bagFetch('/api/bag/projects/' + encodeURIComponent(token) + '/cart-lines', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
    });
}

/**
 * @param {string} token
 * @param {{ template_id: string, migrate_designs?: boolean }} body
 */
export function changeSize(token, body) {
    return bagFetch('/api/bag/projects/' + encodeURIComponent(token) + '/change-size', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            template_id: body.template_id,
            migrate_designs: body.migrate_designs !== false,
        }),
        timeoutMs: GENERATION_TIMEOUT_MS,
    });
}
